import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlMatcher, PreloadingStrategy, Route } from '@angular/router';
import { isEmbed, isExtension, isNativeWindow } from '@local/common-web';
import { PageNotFoundComponent } from '@shared/components/page-not-found/page-not-found.component';
import { Guard } from '@shared/guards';
import { AppComponent } from '../app.component';
import { AttributesResolver } from './attributes-resolver.service';
import { ExternalLinkResolver } from './external-link.resolver';
import { Observable, of } from 'rxjs';
import { UnleashProviderGuard } from '../bar/routes/guards/can-activate-unleash-provider.guard';

let routes: Routes = [];

const getPlatformType = () => {
  if (isExtension()) {
    return 'Extension';
  }
  if (isEmbed()) {
    return 'Embed';
  }
  return 'App';
};

export class CustomPreloadingStrategy implements PreloadingStrategy {
  preload(route: Route, load: () => Observable<any>): Observable<any> {
    return route.data && route.data['preload'] && route.data['platforms']?.indexOf(getPlatformType()) > -1 ? load() : of(null);
  }
}

const onBoardingMatcher: UrlMatcher = (s, g, r) => {
  if (
    s[0]?.path == 'setup' ||
    s[0]?.path == 'signup' ||
    s[0]?.path == 'signin' ||
    s[0]?.path == 'init-tenant' ||
    s[0]?.path == 'empty' ||
    s[0]?.path == 'pricing' ||
    s[0]?.path == 'pricing-completed' ||
    s[0]?.path == 'desktop' ||
    s[0]?.path == 'error' ||
    s[0]?.path == 'select-account' ||
    s[0]?.path == 'user-created' ||
    s[0]?.path == 'join-workspace' ||
    s[0]?.path == 'install-extension'
  ) {
    return { consumed: [] };
  }

  return;
};

if (isNativeWindow()) {
  routes = [
    {
      matcher: onBoardingMatcher,
      loadChildren: () => import('../onboarding-native/onboarding.module').then((m) => m.OnboardingModule),
      data: { moduleName: 'onboarding-native' },
      resolve: { attribute: AttributesResolver },
    },
  ];
} else {
  routes = [
    {
      matcher: onBoardingMatcher,
      loadChildren: () => import('../onboarding-web/onboarding.module').then((m) => m.OnboardingModule),
      data: { moduleName: 'onboarding-web' },
      resolve: { attribute: AttributesResolver },
    },
  ];
}

routes = [
  ...routes,
  {
    path: 'developer',
    loadChildren: () => import('../developer/developer.module').then((m) => m.DeveloperModule),
    resolve: { attribute: AttributesResolver },
  },
  {
    path: 'install-slack',
    loadChildren: () => import('../install-slack/install-slack.module').then((m) => m.SlackInstallModule),
  },
  {
    path: 'install-zendesk',
    loadChildren: () => import('../install-zendesk/install-zendesk.module').then((m) => m.ZendeskInstallModule),
  },
  {
    path: 'external-link',
    resolve: { ['external-link']: ExternalLinkResolver },
    component: AppComponent,
  }, // Never navigate to other route, just handles the opening of the url provided in the queryParam
  {
    path: 'assistant-incontext',
    loadComponent: () =>
      import('../bar/views/assistant-incontext/components/assistant-incontext/assistant-incontext.component').then(
        (m) => m.AssistantIncontextComponent
      ),
  },
  {
    path: 'test-assistant-incontext',
    loadComponent: () =>
      import('../bar/views/assistant-incontext/components/test-assistant-incontext/test-assistant-incontext.component').then(
        (m) => m.TestAssistantIncontextComponent
      ),
    canActivate: [UnleashProviderGuard],
  },
  {
    matcher: (s, g) => {
      return { consumed: [] };
    },
    loadChildren: () => import('../bar/main.module').then((m) => m.MainModule),
    data: { moduleName: `main-${isNativeWindow() ? 'native' : 'web'}` },
    resolve: { attribute: AttributesResolver },
    canActivate: [Guard],
  },
  {
    path: '**',
    component: PageNotFoundComponent,
    resolve: { attribute: AttributesResolver },
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: location.protocol.startsWith('file'),
      enableTracing: false,
      preloadingStrategy: CustomPreloadingStrategy,
    }),
  ],
  providers: [CustomPreloadingStrategy],
  exports: [RouterModule],
})
export class AppRoutingModule {}
